import { useCallback, useEffect, useRef, useState } from 'react';
import { NOTE_SAVE_DEBOUNCE_MS, makeNoteId } from './drawerFormat.js';

const noteList = (review) => (Array.isArray(review?.notes) ? review.notes : []);

const noteTime = (note) => new Date(note?.updatedAt || note?.ts || 0).getTime() || 0;

const normalizeNoteText = (text) => String(text || '').trim();

const draftTextMatches = (noteText, draftTexts) => {
  const candidate = normalizeNoteText(noteText);
  return candidate && draftTexts.some((text) => {
    const draft = normalizeNoteText(text);
    return draft && (candidate === draft || draft.startsWith(candidate) || candidate.startsWith(draft));
  });
};

const findActiveDraft = (notes, currentUserName, draftTexts = []) => (
  [...notes]
    .filter((note) => (
      note.status === 'draft'
      && note.author === currentUserName
      && draftTextMatches(note.text, draftTexts)
    ))
    .sort((a, b) => noteTime(b) - noteTime(a))[0] || null
);

export const useVarianceDrawerNotes = ({
  row,
  review,
  setNotes,
  onReviewChange,
  currentUserName,
}) => {
  const [noteDraft, setNoteDraft] = useState('');
  const [draftNoteId, setDraftNoteId] = useState(null);
  const noteSaveTimerRef = useRef(null);
  const lastPersistedDraftTextRef = useRef('');

  useEffect(() => {
    setNoteDraft('');
    setDraftNoteId(null);
    lastPersistedDraftTextRef.current = '';
    return () => clearTimeout(noteSaveTimerRef.current);
  }, [row?.bac]);

  useEffect(() => {
    if (!draftNoteId) return;
    const notes = noteList(review);
    if (notes.some((note) => note.id === draftNoteId)) return;
    const replacementDraft = findActiveDraft(notes, currentUserName, [lastPersistedDraftTextRef.current, noteDraft]);
    if (replacementDraft?.id) setDraftNoteId(replacementDraft.id);
  }, [currentUserName, draftNoteId, noteDraft, review]);

  const persistDraft = useCallback((text, status = 'draft') => {
    if (!row) return null;
    const trimmed = text.trim();
    const existing = noteList(review);
    const activeDraftId = existing.some((note) => note.id === draftNoteId)
      ? draftNoteId
      : findActiveDraft(existing, currentUserName, [lastPersistedDraftTextRef.current, trimmed])?.id || null;
    if (!trimmed && !activeDraftId) return null;
    if (!trimmed && activeDraftId) {
      const next = setNotes(row, existing.filter((note) => note.id !== activeDraftId));
      onReviewChange(row.bac, next);
      setDraftNoteId(null);
      lastPersistedDraftTextRef.current = '';
      return next;
    }
    if (!activeDraftId) {
      const now = new Date().toISOString();
      const note = {
        id: makeNoteId(),
        ts: now,
        updatedAt: now,
        text: trimmed,
        author: currentUserName,
        status,
      };
      const next = setNotes(row, [...existing, note]);
      onReviewChange(row.bac, next);
      if (status === 'draft') {
        setDraftNoteId(note.id);
        lastPersistedDraftTextRef.current = trimmed;
      } else {
        lastPersistedDraftTextRef.current = '';
      }
      return next;
    }
    const updated = existing.map((note) => (
      note.id === activeDraftId
        ? { ...note, text: trimmed, status }
        : note
    ));
    const next = setNotes(row, updated);
    onReviewChange(row.bac, next);
    lastPersistedDraftTextRef.current = status === 'draft' ? trimmed : '';
    return next;
  }, [currentUserName, draftNoteId, onReviewChange, review, row, setNotes]);

  const scheduleNoteSave = useCallback((text) => {
    clearTimeout(noteSaveTimerRef.current);
    noteSaveTimerRef.current = setTimeout(() => persistDraft(text), NOTE_SAVE_DEBOUNCE_MS);
  }, [persistDraft]);

  const sealCurrentDraft = useCallback((overrideText = null) => {
    if (!row) return;
    clearTimeout(noteSaveTimerRef.current);
    persistDraft(overrideText ?? noteDraft, 'final');
    setDraftNoteId(null);
    setNoteDraft('');
  }, [noteDraft, persistDraft, row]);

  const handleEditNote = useCallback((note) => {
    if (!note || note.author === 'System' || note.status === 'system') return;
    clearTimeout(noteSaveTimerRef.current);
    setDraftNoteId(note.id || null);
    setNoteDraft(note.text || '');
    lastPersistedDraftTextRef.current = note.status === 'draft' ? (note.text || '') : '';
  }, []);

  const handleDeleteNote = useCallback((note) => {
    if (!row || !note || note.author === 'System' || note.status === 'system') return;
    clearTimeout(noteSaveTimerRef.current);
    const existing = noteList(review);
    const nextNotes = existing.filter((candidate) => (
      candidate.id ? candidate.id !== note.id : candidate.ts !== note.ts
    ));
    const next = setNotes(row, nextNotes);
    onReviewChange(row.bac, next);
    if (draftNoteId && draftNoteId === note.id) {
      setDraftNoteId(null);
      setNoteDraft('');
      lastPersistedDraftTextRef.current = '';
    }
  }, [draftNoteId, onReviewChange, review, row, setNotes]);

  return {
    noteDraft,
    activeDraftNoteId: draftNoteId,
    setNoteDraft,
    scheduleNoteSave,
    sealCurrentDraft,
    handleEditNote,
    handleDeleteNote,
  };
};
